import {AddressTypeV4Enum, PrincipalFavoritePlaceEnum} from 'domain/usecases';
import {ProfileEnum} from 'presentation/constants';
import React, {useCallback, useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {formatText} from 'presentation/utils/formatText';
import {formatAddress} from 'presentation/utils';
import {useDeleteFavoriteAddressMutation} from '../mutations/useDeleteFavoriteAddressMutation';
import {useGetFavoriteAddressesQuery} from '../queries/useGetFavoriteAddressesQuery';
import {FavoriteAddressesItem} from '../types';
import {useActiveDependents} from './useActiveDependents';
import {ActiveDependentItem, AssignSuccessData, EditSuccessData} from './types';
import {postAssignFavoriteAddressToDependents} from '../services/postAssignFavoriteAddressToDependents/assignFavoriteAddressToDependents';
import {translations} from 'presentation/translations-new';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';

const {
	editFavoritePlace: {successModal},
} = translations['pt-br'].privateFlow.bookingFlow.location;

export function useListFavoriteAddresses() {
	const navigate = useNavigate();
	const location = useLocation();
	const {pathname} = location;
	const {activeDependents, hasActiveDependents, isHolder} = useActiveDependents();
	const [anchorContextMenuEl, setAnchorContextMenuEl] = useState<null | HTMLElement>(null);
	const isMenuOpen = Boolean(anchorContextMenuEl);
	const [selectedItem, setSelectedItem] = useState<FavoriteAddressesItem | null>(null);
	const [openRemoveFavoriteAddressAlert, setOpenRemoveFavoriteAddressAlert] = useState(false);
	const [openDeleteCasaModal, setOpenDeleteCasaModal] = useState(false);
	const [openDeleteError, setOpenDeleteError] = useState(false);
	const [openDeleteSuccess, setOpenDeleteSuccess] = useState(false);
	const [openAssignToDependent, setOpenAssignToDependent] = useState(false);
	const [openAssignSuccessModal, setOpenAssignSuccessModal] = useState(false);
	const [assignSuccessData, setAssignSuccessData] = useState<AssignSuccessData | null>(null);
	const [isPostingAssignment, setIsPostingAssignment] = useState(false);
	const [openAssignError, setOpenAssignError] = useState(false);
	const [openEditSuccessModal, setOpenEditSuccessModal] = useState(
		() => !!location.state?.editSuccess,
	);
	const [editSuccessData, setEditSuccessData] = useState<EditSuccessData | null>(
		() => location.state?.editSuccess ?? null,
	);

	const {
		beneficiary: {idadeC},
	} = useAccountDataStore(state => state.accountData);

	const {data, isLoading} = useGetFavoriteAddressesQuery();
	const {
		mutate: deleteFavoriteAddress,
		isPending: isDeletingFavoriteAddress,
		isSuccess: isDeleteFavoriteAddressSuccess,
		isError: isDeletingFavoriteAddressError,
	} = useDeleteFavoriteAddressMutation();

	const favoriteAddresses = data ?? [];

	const handleContextMenuOpen = useCallback(
		(event: React.MouseEvent<HTMLElement>, item: FavoriteAddressesItem) => {
			event.stopPropagation();
			setAnchorContextMenuEl(event.currentTarget);
			setSelectedItem(item);
		},
		[setAnchorContextMenuEl, setSelectedItem],
	);

	const handleContextMenuClose = useCallback(() => {
		setAnchorContextMenuEl(null);
	}, [setAnchorContextMenuEl]);

	const goToProfile = useCallback(() => {
		navigate(ProfileEnum.PROFILE);
	}, [navigate]);

	const [loadingCardClickId, setLoadingCardClickId] = useState<string | null>(null);

	const handleCardClick = useCallback(
		async (item: FavoriteAddressesItem) => {
			if (!item?.address) return;
			setLoadingCardClickId(item.id);
			try {
				goToProfile();
			} finally {
				setLoadingCardClickId(null);
			}
		},
		[goToProfile],
	);

	const handleOpenRemoveFavoriteAddressAlert = () => {
		setOpenRemoveFavoriteAddressAlert(true);
	};

	const handleCloseRemoveFavoriteAddressAlert = () => {
		setOpenRemoveFavoriteAddressAlert(false);
	};

	const handleEdit = useCallback(() => {
		navigate(ProfileEnum.FAVORITE_ADDRESSES_EDIT, {
			state: {item: selectedItem, from: pathname},
		});
	}, [navigate, pathname, selectedItem]);

	const handleAddNewAddress = () => {
		navigate(ProfileEnum.FAVORITE_ADDRESSES_ADD, {
			state: {from: pathname},
		});
	};

	const handleDelete = useCallback(() => {
		if (selectedItem?.address.flPrincipal === PrincipalFavoritePlaceEnum.YES) {
			setOpenDeleteCasaModal(true);
		} else {
			handleOpenRemoveFavoriteAddressAlert();
		}
	}, [selectedItem]);

	const handleConfirmDeleteCasa = useCallback(() => {
		setOpenDeleteCasaModal(false);

		navigate(ProfileEnum.FAVORITE_ADDRESSES_ADD, {
			state: {from: pathname, isCasaDeletion: true},
		});
	}, []);

	const handleCloseDeleteCasaModal = useCallback(() => {
		setOpenDeleteCasaModal(false);
	}, []);

	const handleDeleteConfirmed = useCallback(() => {
		if (selectedItem) {
			deleteFavoriteAddress({
				sequence: selectedItem.address.cdBeneficiarioEnderecoFavorito,
			});
		}
	}, [selectedItem, deleteFavoriteAddress]);

	const handleOpenDeleteError = () => {
		setOpenDeleteError(true);
	};

	const handleCloseDeleteError = () => {
		setOpenDeleteError(false);
	};

	const handleOpenDeleteSuccess = () => {
		setOpenDeleteSuccess(true);
	};

	const handleCloseDeleteSuccess = () => {
		setOpenDeleteSuccess(false);
	};

	useEffect(() => {
		if (isDeleteFavoriteAddressSuccess) {
			handleCloseRemoveFavoriteAddressAlert();
			handleOpenDeleteSuccess();
		}
	}, [isDeleteFavoriteAddressSuccess]);

	useEffect(() => {
		if (isDeletingFavoriteAddressError) {
			handleOpenDeleteError();
		}
	}, [isDeletingFavoriteAddressError]);

	useEffect(() => {
		if (location.state?.editSuccess) {
			navigate(location.pathname, {replace: true, state: null});
		}
	}, []);

	const showAssignToDependent =
		isHolder && hasActiveDependents && selectedItem?.address.type !== AddressTypeV4Enum.BILLING;

	const handleOpenAssignToDependent = useCallback(() => {
		setOpenAssignToDependent(true);
	}, []);

	const handleConfirmEditMain = useCallback(() => {
		setOpenAssignToDependent(true);

		if (openEditSuccessModal) {
			setSelectedItem(
				favoriteAddresses.find(
					item => item.address.flPrincipal === PrincipalFavoritePlaceEnum.YES,
				) ?? null,
			);
			handleCloseEditSuccessModal();
		}
	}, [favoriteAddresses, openEditSuccessModal]);

	const handleCloseAssignToDependent = useCallback(() => {
		setOpenAssignToDependent(false);
		setSelectedItem(null);
	}, []);

	const handleConfirmAssignToDependent = useCallback(
		async (selected: ActiveDependentItem[]) => {
			if (!selectedItem) {
				setOpenAssignToDependent(false);
				return;
			}

			setIsPostingAssignment(true);
			try {
				await postAssignFavoriteAddressToDependents({
					item: selectedItem,
					selected,
				});
				setOpenAssignToDependent(false);
				setAssignSuccessData({
					addressLabel: formatAddress({
						address: selectedItem.address.nmLogradouro,
						number: String(selectedItem.address.nuEndereco),
						district: selectedItem.address.nmBairro,
						city: selectedItem.address.nmCidade,
						state: selectedItem.address.nmUf,
					}),
					dependentNames: selected.map(d => formatText(d.nmUsuarioC)),
				});
				setOpenAssignSuccessModal(true);
			} catch {
				setOpenAssignError(true);
			} finally {
				setIsPostingAssignment(false);
			}
		},
		[selectedItem],
	);

	const handleCloseAssignSuccessModal = useCallback(() => {
		setOpenAssignSuccessModal(false);
		setAssignSuccessData(null);
	}, []);

	const handleCloseAssignError = useCallback(() => {
		setOpenAssignError(false);
	}, []);

	const handleCloseEditSuccessModal = useCallback(() => {
		setOpenEditSuccessModal(false);
		setEditSuccessData(null);
	}, []);

	const getSortedFavoriteAddresses = (items: FavoriteAddressesItem[]) =>
		[...items].sort((a, b) => {
			const aIsMain = a.address.flPrincipal === PrincipalFavoritePlaceEnum.YES ? 0 : 1;
			const bIsMain = b.address.flPrincipal === PrincipalFavoritePlaceEnum.YES ? 0 : 1;
			return aIsMain - bIsMain;
		});

	const isCasaWithDependentAssignment = editSuccessData?.isCasa && showAssignToDependent;
	const successModalVariations = {
		assignDependent: {
			descriptionSuffix: successModal.descriptionCasaSuffix,
			primaryAction: handleConfirmEditMain,
			primaryButtonLabel: successModal.updateButton,
			showSecondaryAction: true,
			secondaryAction: handleCloseEditSuccessModal,
			secondaryButtonLabel: successModal.cancelUpdateButton,
		},

		default: {
			descriptionSuffix: editSuccessData?.isCasa
				? successModal.descriptionSuffixWithoutDependents
				: successModal.descriptionSuffix,
			primaryAction: handleCloseEditSuccessModal,
			primaryButtonLabel: successModal.closeButton,
			showSecondaryAction: false,
			secondaryAction: undefined,
			secondaryButtonLabel: undefined,
		},
	};

	const successModalConfig =
		successModalVariations[isCasaWithDependentAssignment ? 'assignDependent' : 'default'];

	const isAdult = idadeC >= 18;

	const handleDeleteConditional =
		selectedItem?.address.type === AddressTypeV4Enum.BILLING ? undefined : handleDelete;

	return {
		goToProfile,
		listItems: getSortedFavoriteAddresses(favoriteAddresses),
		handleContextMenuOpen,
		handleContextMenuClose,
		handleCardClick,
		handleEdit: handleEdit,
		handleDelete: handleDeleteConditional,
		handleAddNewAddress,
		handleCloseRemoveFavoriteAddressAlert,
		handleDeleteConfirmed,
		handleCloseDeleteError,
		handleCloseDeleteSuccess,
		openRemoveFavoriteAddressAlert,
		openDeleteCasaModal,
		handleConfirmDeleteCasa,
		handleCloseDeleteCasaModal,
		openDeleteError,
		openDeleteSuccess,
		anchorContextMenuEl,
		isMenuOpen,
		isLoading,
		isDeletingFavoriteAddress,
		loadingCardClickId,
		showAssignToDependent,
		activeDependents,
		openAssignToDependent,
		openAssignSuccessModal,
		assignSuccessData,
		isPostingAssignment,
		openAssignError,
		handleOpenAssignToDependent,
		handleCloseAssignToDependent,
		handleConfirmAssignToDependent,
		handleCloseAssignSuccessModal,
		handleCloseAssignError,
		openEditSuccessModal,
		editSuccessData,
		handleCloseEditSuccessModal,
		handleConfirmEditMain,
		successModalConfig,
		isAdult,
		isHolder,
	};
}
